import { IconButton, Box, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { FormikProps } from "formik";
import { ProfileFormValues } from "../hooks/useProfileScreen"; // Import your form values type
import { toast } from "react-toastify";

interface Image {
  file?: Blob | MediaSource | ArrayBuffer;
  preview: string;
  fileName?: string;
  mimetype?: string;
}
interface CompanyLogoProps {
  logo: Image;
  onLogoChange: (logo: Image) => void;
  formik: FormikProps<ProfileFormValues>;
}

const CompanyLogo = ({ logo, onLogoChange, formik }: CompanyLogoProps) => {
  const handleLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const image = new Image();
      image.src = URL.createObjectURL(file);

      image.onload = async () => {
        const { width, height } = image;
        // Validate Aspect Ratio (1:1) & Minimum Size (100x100)
        if (width !== height) {
          toast.error("Logo must have a 1:1 aspect ratio.");
          return;
        }
        if (width < 100 || height < 100) {
          toast.error("Logo dimensions must be at least 100px × 100px.");
          return;
        }

        // Convert file to buffer for storage
        const buffer = await file.arrayBuffer();
        const data = {
          file: buffer,
          preview: URL.createObjectURL(file),
          fileName: "logo",
          mimetype: file.type,
        };
        onLogoChange(data);
      };
    }
    event.target.value = "";
  };

  const handleRemoveLogo = () => {
    onLogoChange({
      preview: "",
      file: undefined,
      fileName: undefined,
      mimetype: undefined,
    });
    formik.setFieldTouched("logo", true, false);
  };

  const error = formik.touched.logo && formik.errors.logo;

  return (
    <Box mb={3}>
      <Typography variant="h6" mb={2} fontFamily="Maven Pro, sans-serif">
        Company Logo*
        <Typography variant="caption" color="text.primary" ml={2} fontSize={14}>
        (1:1 aspect ratio , min 100px X 100px , Format : .jpg, .jpeg, .png , Max 10MB)
        </Typography>
      </Typography>
      <Box
        sx={{
          width: 419,
          height: 250,
          border: `1px dashed ${error ? "red" : "grey"}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {logo?.preview !== "" ? (
          <>
            <img
              src={logo.preview}
              alt="Company Logo"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                padding: "50px",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                bgcolor: "#134974",
                p: 1,
              }}
            >
              <IconButton size="small" component="label">
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoChange}
                />
                <Box display="flex" gap={1}>
                  <EditIcon sx={{ color: "white" }} />
                  <Typography sx={{ color: "white" }}>Edit</Typography>
                </Box>
              </IconButton>
              <IconButton size="small" onClick={handleRemoveLogo}>
                <Box display="flex" gap={1}>
                  <DeleteIcon sx={{ color: "white" }} />
                  <Typography sx={{ color: "white" }}>Remove</Typography>
                </Box>
              </IconButton>
            </Box>
          </>
        ) : (
          <IconButton component="label">
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleLogoChange}
            />
            <AddIcon />
          </IconButton>
        )}
      </Box>

      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block" }}
        >
          {formik.errors.logo?.preview}
        </Typography>
      )}
    </Box>
  );
};

export default CompanyLogo;
